import { useMemo, useState, useEffect } from "react";
import type { ReactNode } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { type FilterOptionOption, Select } from "ui/select";
import { Spinner } from "ui/spinner";

import SelectedItems from "components/partials/selected-items/selected-items";
import SelectedItem from "components/partials/selected-items/selected-item/selected-item";

import type { TouchpointAttributes } from "hooks/api/use-touchpoints";
import { getTouchpoint } from "generated/api/touchpoint";
import { normalizeTouchpointListResponse } from "utilities/api-mappers";

import { filterTouchpoints } from "components/partials/selected-items/selected-items.helper";

const touchpointApi = getTouchpoint();

type CampaignOption = { label: string; value: string };

type TouchpointVersionOption = {
  label: ReactNode;
  value: string; // touchpointVersionId
  touchpoint: TouchpointAttributes;
  shortValueToDisplay: string;
};

const AddTouchpointsForm = ({
  clientId,
  batchId,
  currentCampaignsOptions,
  setAddingToBatchSubmitIsDisabled,
}: {
  clientId: string;
  batchId: string;
  currentCampaignsOptions: CampaignOption[];
  setAddingToBatchSubmitIsDisabled: (isDisabled: boolean) => void;
}) => {
  const { control, setValue } = useFormContext();

  const [touchpoints, setTouchpoints] = useState<TouchpointAttributes[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTouchpoints, setSelectedTouchpoints] = useState<TouchpointAttributes[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignOption | null>(null);

  useEffect(() => {
    setValue("chosenTouchpoints", []);
    setAddingToBatchSubmitIsDisabled(!selectedCampaign);

    const fetchTouchpoints = async () => {
      if (!selectedCampaign) return;

      setIsLoading(true);

      const response = await touchpointApi.getAllTouchpointsForCampaign(
        { clientId, campaignId: selectedCampaign.value },
        { unpaged: true },
      );
      const normalized = normalizeTouchpointListResponse(
        response,
        clientId,
        selectedCampaign.value,
      );

      const filteredTouchpoints = filterTouchpoints(normalized.items).filter(
        (touchpoint) => touchpoint.touchpointBatchId !== batchId,
      );

      setTouchpoints(filteredTouchpoints);
      setSelectedTouchpoints([]);
      setValue("chosenTouchpointsForBatch", null);
      setIsLoading(false);
    };

    selectedCampaign && fetchTouchpoints();
  }, [selectedCampaign, clientId, batchId, setValue, setAddingToBatchSubmitIsDisabled]);

  const touchpointsOptions = useMemo(() => {
    setAddingToBatchSubmitIsDisabled(!touchpoints.length);

    return touchpoints
      .map((touchpoint): TouchpointVersionOption | null => {
        const versionId = touchpoint.latestVersion?.id;
        if (!versionId) return null;
        return {
        label: (
          <SelectedItem
            name={touchpoint.name}
            latestVersion={touchpoint.latestVersion}
            type={touchpoint.type}
            showStatus
            showBatch
            touchpointBatchName={touchpoint.touchpointBatchName}
          />
        ),
          value: versionId,
          touchpoint,
          shortValueToDisplay: touchpoint.name,
        };
      })
      .filter((v): v is TouchpointVersionOption => Boolean(v));
  }, [touchpoints]);

  const filterTouchpointOption = (
    option: FilterOptionOption<unknown>,
    inputValue: string,
  ): boolean => {
    if (!inputValue) return true;

    const touchpointOption = option.data as TouchpointVersionOption;
    return touchpointOption.touchpoint.name.toLowerCase().includes(inputValue.toLowerCase());
  };

  return (
    <>
      <Controller
        name="chosenCampaign"
        control={control}
        rules={{ required: true }}
        render={({ field }) => {
          return (
            <Select
              {...field}
              label="Campaigns"
              ariaLabel="Choose campaign"
              selectName={`campaigns-select-${field.name}`}
              options={currentCampaignsOptions}
              required
              value={field.value}
              onChange={(option) => {
                setSelectedCampaign(option);
                field.onChange(option);
              }}
            />
          );
        }}
      />
      {/* Fix height for loading state */}
      <div className="flex flex-col min-h-[240px] gap-3">
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <Spinner />
          </div>
        ) : (
          <>
            <Controller
              name="chosenTouchpoints"
              control={control}
              rules={{ required: true }}
              render={({ field }) => {
                return (
                  <Select
                    {...field}
                    label="Creatives"
                    required
                    placeholder={
                      touchpointsOptions.length
                        ? undefined
                        : "There are no reviewable creatives in this campaign"
                    }
                    ariaLabel="Choose creatives"
                    selectName={`touchpoints-select-${field.name}`}
                    options={touchpointsOptions.length ? touchpointsOptions : []}
                    disabled={!touchpointsOptions.length}
                    multiple
                    filterOption={filterTouchpointOption}
                    value={field.value}
                    onChange={(selected) => {
                      setSelectedTouchpoints(selected.map((o) => o.touchpoint));
                      field.onChange(selected);
                    }}
                  />
                );
              }}
            />

            <SelectedItems selectedList={selectedTouchpoints} showStatus showBatch />
          </>
        )}
      </div>
    </>
  );
};

export default AddTouchpointsForm;
